import { useState, useMemo } from 'react'

import type { ReactElement, ChangeEvent } from 'react'
import type { ITemplate } from '~/types/template'

import TemplateModal from './Modal'

import { useTemplateListQuery } from '../queries'

import ContentLayout from '~/components/ContentLayout'
import Filters, { FiltersContent } from '~/components/Filters'
import SearchInput from '~/components/Inputs/Basic/SearchInput'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import Layout from '~/components/Layouts/Layout'
import Modal from '~/components/Modal'
import Section from '~/components/Section'
import TemplateCard from '~/components/TemplateCard'
import { DEFAULT_ITEMS_PER_PAGE_VALUE } from '~/const'
import { platformOptions } from '~/const/options'
import { orientationOptions } from '~/const/template'
import { debounce } from '~/utils/function'

import './styles.scss'

const initialFilters = {
  orientation: orientationOptions[0],
  platform: platformOptions[0],
  title: '',
}

interface ISelectedTemplate {
  key: string
  version: string
}

const GridView = (): ReactElement => {
  const [selectedOrientation, setSelectedOrientation] = useState(initialFilters.orientation)

  const [selectedPlatform, setSelectedPlatform] = useState(initialFilters.platform)

  const [search, setSearch] = useState(initialFilters.title)

  const [selectedTemplate, setSelectedTemplate] = useState<ISelectedTemplate | undefined>()

  const { data: templates, isLoading: isTemplatesLoading } = useTemplateListQuery({
    'device-type': selectedPlatform.value,
    orientation: selectedOrientation.value,
    size: DEFAULT_ITEMS_PER_PAGE_VALUE,
    ...(search ? { title: search } : {}),
  })

  const handleOrientationChanged = (value: IOption) => {
    setSelectedOrientation(value)
  }

  const handlePlatformChanged = (value: IOption) => {
    setSelectedPlatform(value)
  }

  const handleSearchTextChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setSearch(evt.target.value)
  }

  const debouncedSearchTextChange = useMemo(() => debounce(handleSearchTextChange, 300), [])

  const toggleTemplateModal = (selectedTemplate: ISelectedTemplate | undefined) => () =>
    setSelectedTemplate(selectedTemplate)

  const selectedTemplateDetails = (): ITemplate | undefined => {
    if (selectedTemplate && templates?.data) {
      return templates.data.find(
        ({ template_key, version }) => template_key === selectedTemplate.key && version === selectedTemplate.version
      )
    }
    return undefined
  }

  return (
    <div className="templates-grid-view">
      <Filters className="templates-filters">
        <FiltersContent>
          <div className="filters-row">
            <div className="inputs-container">
              <SelectInput
                className="select-filter"
                isSearchable
                label="Orientation"
                onChange={(data: unknown) => handleOrientationChanged(data as IOption)}
                options={orientationOptions}
                value={orientationOptions.find((option) => option.value === selectedOrientation.value)}
              />
              <SelectInput
                className="select-filter"
                isSearchable
                label="Platform"
                onChange={(data: unknown) => handlePlatformChanged(data as IOption)}
                options={platformOptions}
                value={platformOptions.find((option) => option.value === selectedPlatform.value)}
              />
            </div>
          </div>
        </FiltersContent>
      </Filters>
      <Layout className="templates-view" withPadding>
        <ContentLayout loading={isTemplatesLoading}>
          <Section className="section">
            <div className="search-container">
              <SearchInput onChange={debouncedSearchTextChange} />
            </div>
            <div className="template-card-container">
              {templates?.data?.map(
                (
                  { capabilities, device_type, navigation_target_outputs, orientation, template_key, title, version },
                  index
                ) => (
                  <TemplateCard
                    capabilities={capabilities}
                    device={device_type}
                    key={`template-card-${index}`}
                    onClick={toggleTemplateModal({ key: template_key, version })}
                    orientation={orientation}
                    targets={navigation_target_outputs || []}
                    title={title}
                    version={version}
                  />
                )
              )}
            </div>
          </Section>
        </ContentLayout>
        <Modal
          className="template-card-modal"
          isOpen={!!selectedTemplate}
          onCloseModal={toggleTemplateModal(undefined)}
        >
          <TemplateModal templateDetails={selectedTemplateDetails()} />
        </Modal>
      </Layout>
    </div>
  )
}

export default GridView
